import { z } from 'zod';
import {
  StopRegistryDisplayType,
  StopRegistryInfoSpotType,
  StopRegistryIntendedUser,
} from '../../../../../generated/graphql';
import { createNullableEnum, nullableBoolean } from '../../../../../utils';
import { InfoSpotPurposeEnum } from './InfoSpotPurpose';

export const itemSizeUiState = z.enum([
  'UNKNOWN', // Width = null, height = null; Default state, or explicitly set.
  'EXISTING', // A known size from a list, or previously set width != null, height != null
  'NEW', // A new size, width and height start as null, but should both be set
]);

export const itemSizeSchema = z.object({
  uiState: itemSizeUiState,
  width: z.number().min(0).nullable(),
  height: z.number().min(0).nullable(),
});

export const infoSpotPurposeSchema = z.object({
  purposeType: z.nativeEnum(InfoSpotPurposeEnum),
  customPurpose: z.string().nullable(),
});

export const posterSchema = z.object({
  id: z.string().nullable(),
  label: infoSpotPurposeSchema,
  lines: z.string().nullable(),
  size: itemSizeSchema,
  sortOrder: z.number().nullable(),
  toBeDeletedPoster: z.boolean(),
});

export const infoSpotSchema = z.object({
  infoSpotId: z.string().nullable(),
  backlight: nullableBoolean,
  description: z.object({
    lang: z.string().nullable(),
    value: z.string().nullable(),
  }),
  displayType: createNullableEnum<StopRegistryDisplayType>(),
  floor: z.string().nullable(),
  label: z.string().nullable(),
  infoSpotLocations: z.array(z.string().nullable()).nullable(),
  infoSpotType: createNullableEnum<StopRegistryInfoSpotType>(),
  intendedUser: createNullableEnum<StopRegistryIntendedUser>(),
  railInformation: z.string().nullable(),
  speechProperty: nullableBoolean,
  zoneLabel: z.string().nullable(),
  poster: posterSchema.array().nullable(),
  size: itemSizeSchema,
  sortOrder: z.number().nullable(),
  toBeDeleted: z.boolean(),
});

export const InfoSpotsFormSchema = z.object({
  infoSpots: z.array(infoSpotSchema),
});

export type ItemSizeState = z.infer<typeof itemSizeSchema>;
export type PosterState = z.infer<typeof posterSchema>;
export type InfoSpotPurposeState = z.infer<typeof infoSpotPurposeSchema>;
export type InfoSpotState = z.infer<typeof infoSpotSchema>;
export type InfoSpotsFormState = z.infer<typeof InfoSpotsFormSchema>;

export const UnknownMenuItem: ItemSizeState = {
  uiState: 'UNKNOWN',
  width: null,
  height: null,
};

export const NewMenuItem: ItemSizeState = {
  uiState: 'NEW',
  width: null,
  height: null,
};
